import type { DurationUnit, RawInputs, ValidationIssue } from '../types';
import { CONVERSION_CONVENTION } from '../calculator/units';

interface Props {
  inputs: RawInputs;
  issues: ValidationIssue[];
  onChange: (patch: Partial<RawInputs>) => void;
  onReset: () => void;
}

const UNITS: DurationUnit[] = ['days', 'months', 'years'];

function fieldStatus(issues: ValidationIssue[], field: keyof RawInputs): 'error' | 'warning' | undefined {
  const relevant = issues.filter((i) => i.field === field);
  if (relevant.some((i) => i.severity === 'error')) return 'error';
  if (relevant.length > 0) return 'warning';
  return undefined;
}

export function InputPanel({ inputs, issues, onChange, onReset }: Props) {
  const status = (field: keyof RawInputs) => fieldStatus(issues, field);

  return (
    <div className="panel input-panel">
      <div className="panel-head-row">
        <h3 className="panel-title">Your scenario</h3>
        <button type="button" className="link-button" onClick={onReset}>
          Clear inputs
        </button>
      </div>

      <div className="field">
        <label htmlFor="shelf-life">Desired real-time shelf life</label>
        <div className="field-row">
          <input
            id="shelf-life"
            inputMode="decimal"
            className={`input input-number ${status('shelfLifeValue') ?? ''}`}
            value={inputs.shelfLifeValue}
            onChange={(e) => onChange({ shelfLifeValue: e.target.value })}
            aria-describedby="shelf-life-help"
          />
          <select
            className="input input-select"
            aria-label="Shelf life unit"
            value={inputs.shelfLifeUnit}
            onChange={(e) => onChange({ shelfLifeUnit: e.target.value as DurationUnit })}
          >
            {UNITS.map((unit) => (
              <option key={unit} value={unit}>
                {unit}
              </option>
            ))}
          </select>
        </div>
        <p className="field-help" id="shelf-life-help">
          Converted internally to days. {CONVERSION_CONVENTION.summary}
        </p>
      </div>

      <div className="field-pair">
        <div className="field">
          <label htmlFor="ref-temp">
            Reference temperature <span className="symbol">T_RT</span>
          </label>
          <div className="field-row">
            <input
              id="ref-temp"
              inputMode="decimal"
              className={`input input-number ${status('referenceTempC') ?? ''}`}
              value={inputs.referenceTempC}
              onChange={(e) => onChange({ referenceTempC: e.target.value })}
            />
            <span className="unit-suffix">°C</span>
          </div>
        </div>

        <div className="field">
          <label htmlFor="acc-temp">
            Accelerated temperature <span className="symbol">T_AA</span>
          </label>
          <div className="field-row">
            <input
              id="acc-temp"
              inputMode="decimal"
              className={`input input-number ${status('acceleratedTempC') ?? ''}`}
              value={inputs.acceleratedTempC}
              onChange={(e) => onChange({ acceleratedTempC: e.target.value })}
            />
            <span className="unit-suffix">°C</span>
          </div>
        </div>
      </div>
      <p className="field-help">
        Both temperatures are yours to choose. This tool does not suggest, rank, or endorse a value for either one.
      </p>

      <div className="field field-assumption">
        <label htmlFor="q10">
          Q10 <span className="assumption-flag">assumption</span>
        </label>
        <div className="field-row">
          <input
            id="q10"
            inputMode="decimal"
            className={`input input-number ${status('q10') ?? ''}`}
            value={inputs.q10}
            onChange={(e) => onChange({ q10: e.target.value })}
            aria-describedby="q10-help"
          />
        </div>
        <p className="field-help" id="q10-help">
          Pre-filled with 2.0 so the tool opens with a working example. That is a starting value for demonstration, not a
          recommended or universally correct one. The sensitivity table below shows what this single number does to your
          result.
        </p>
      </div>

      <div className="field">
        <label htmlFor="start-date">Study start date — optional</label>
        <div className="field-row">
          <input
            id="start-date"
            type="date"
            className={`input ${status('startDate') ?? ''}`}
            value={inputs.startDate}
            onChange={(e) => onChange({ startDate: e.target.value })}
          />
        </div>
        <p className="field-help">Used only to project a completion date from the calculated duration.</p>
      </div>
    </div>
  );
}
